import { AnalysisType, Result, ResultLine } from '../../types/types'
import { SampleStatus } from '../../utils/sampleUtils'
import { Mtds } from 'prove-common'
import { ConfirmResultButton } from '../confirm-result/ConfirmResultButton'
import { AttachmentsSection } from './AttachmentsSection'

type AnalysisResultsSectionProps = {
  sampleId: string
  requisitionId: number
  analysisType: AnalysisType
  result: Result | undefined
  sampleStatus: SampleStatus
  isCompromised?: boolean
}

const mapSuggestedInterpretation = ({
  suggestedInterpretation: result
}: ResultLine) => {
  if (result === 'pos') return 'Påvist'
  if (result === 'neg') return 'Ikke påvist'
  if (result === 'inc') return 'Inkonklusiv'
  return '-'
}

const getSubstanceName = (
  analysisType: AnalysisType,
  substanceId?: number
): string | undefined => {
  if (substanceId == null) return undefined
  const foundSubstance = analysisType.substances?.find(
    (analysisTypeSubstance) => analysisTypeSubstance.id === substanceId
  )
  return foundSubstance?.substance?.name
}

const hasAnyRemarks = (resultLines: ResultLine[]): boolean =>
  resultLines.some((line) => line.remarks != null && line.remarks.length > 0)

const hasAnyMeasurementUncertainty = (resultLines: ResultLine[]): boolean =>
  resultLines.some((line) => line.measurementUncertainty != null)

export const AnalysisResultsSection = ({
  sampleId,
  requisitionId,
  analysisType,
  result,
  sampleStatus,
  isCompromised = false
}: AnalysisResultsSectionProps) => {
  return (
    <Mtds.Grid data-gap='4' data-align-content={'start'}>
      {(result?.resultSets == null || result?.resultSets?.length === 0) && (
        <Mtds.Muted>
          Ingen analyseresultater er mottatt for denne delprøven.
        </Mtds.Muted>
      )}
      {result?.resultSets?.map((resultSet, index) => {
        const resultSetStatus = resultSet.status
        const resultLines = resultSet.resultLines || []
        const isConfirmed =
          resultSetStatus === 'POSITIVE' || resultSetStatus === 'NEGATIVE'
        const canConclude =
          !isCompromised &&
          resultSetStatus === 'UNDETERMINED' &&
          sampleStatus === SampleStatus.ready_to_process
        const canEdit =
          !isCompromised &&
          isConfirmed &&
          [
            SampleStatus.ready_to_process,
            SampleStatus.completed,
            SampleStatus.closed
          ].includes(sampleStatus)
        const attachments = resultSet.attachments || []
        const hasAttachments = attachments.length > 0
        const substanceId = resultSet.analysisTypeSubstanceId
        const substanceName =
          substanceId == null
            ? undefined
            : getSubstanceName(analysisType, substanceId)

        const showRemarksColumn = hasAnyRemarks(resultLines)
        const showMeasurementUncertaintyColumn =
          hasAnyMeasurementUncertainty(resultLines)

        let statusTag = null
        if (resultSetStatus === 'NEGATIVE') {
          statusTag = <Mtds.Tag data-color='success'>Ikke påvist</Mtds.Tag>
        } else if (resultSetStatus === 'POSITIVE') {
          statusTag = <Mtds.Tag data-color='danger'>Påvist</Mtds.Tag>
        }

        return (
          <Mtds.Details
            key={resultSet.resultId}
            data-variant='card'
            open={canConclude}
          >
            <Mtds.Details.Summary>
              <Mtds.Flex data-align='center' data-justify='space-between'>
                <p>{substanceName ?? `Analyse ${index + 1}`}</p>
                {statusTag}
              </Mtds.Flex>
            </Mtds.Details.Summary>
            <Mtds.Grid data-gap={8} className='mt-4'>
              {(canConclude || canEdit) && (
                <ConfirmResultButton
                  sampleId={sampleId}
                  requisitionId={requisitionId}
                  resultId={resultSet.resultId}
                  resultStatus={resultSetStatus}
                />
              )}
              {resultLines.length > 0 && (
                <Mtds.Table
                  aria-label='Analyseresultater'
                  className='[&_td]:[overflow-wrap:anywhere]'
                  data-fixed
                  data-mobile={'divided'}
                >
                  <thead>
                    <tr>
                      <th>Analyser for</th>
                      <th>Mengde</th>
                      {showMeasurementUncertaintyColumn && (
                        <th>Måleusikkerhet</th>
                      )}
                      {showMeasurementUncertaintyColumn && (
                        <th>Dekningsfaktor</th>
                      )}
                      <th>Konklusjon</th>
                      {showRemarksColumn && <th>Kommentar</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {resultLines.map((line) => {
                      const isDetected = line.suggestedInterpretation === 'pos'
                      const lineKey = [
                        line.name,
                        line.efsaCode ?? '',
                        line.method,
                        line.amount ?? '',
                        line.unitOfMeasurement,
                        line.measurementUncertainty?.value ?? '',
                        line.measurementUncertainty?.coverageFactor ?? '',
                        line.detectableThreshold ?? '',
                        line.remarks ?? ''
                      ].join('-')
                      const shouldShowRemarksTooltip =
                        line.remarks != null && line.remarks.length > 8
                      return (
                        <tr
                          key={lineKey}
                          className={
                            isDetected
                              ? 'bg-danger-background-tinted text-danger-base-default'
                              : undefined
                          }
                        >
                          <td>
                            <span className='flex items-center gap-2'>
                              {isDetected && (
                                <span className='size-1.5 rounded-full bg-danger-base-default shrink-0 inline-block' />
                              )}
                              {line.name}
                            </span>
                          </td>
                          <td>
                            {line.amount != null
                              ? `${line.amount} ${line.unitOfMeasurement}`
                              : '-'}
                          </td>
                          {showMeasurementUncertaintyColumn && (
                            <td>
                              {line.measurementUncertainty != null
                                ? `${line.measurementUncertainty.value} ${line.unitOfMeasurement}`
                                : '-'}
                            </td>
                          )}
                          {showMeasurementUncertaintyColumn && (
                            <td>
                              {line.measurementUncertainty?.coverageFactor ??
                                '-'}
                            </td>
                          )}
                          <td>{mapSuggestedInterpretation(line)}</td>
                          {showRemarksColumn && (
                            <td>
                              <span
                                className='inline-block max-w-[8ch] truncate'
                                title={
                                  shouldShowRemarksTooltip
                                    ? (line.remarks ?? undefined)
                                    : undefined
                                }
                              >
                                {line.remarks ?? '-'}
                              </span>
                            </td>
                          )}
                        </tr>
                      )
                    })}
                  </tbody>
                </Mtds.Table>
              )}

              {hasAttachments ? (
                <AttachmentsSection
                  resultSet={resultSet}
                  isPositive={resultSetStatus === 'POSITIVE'}
                />
              ) : null}
              {resultLines.length === 0 && !hasAttachments && (
                <Mtds.Muted>Ingen data tilgjengelig</Mtds.Muted>
              )}
            </Mtds.Grid>
          </Mtds.Details>
        )
      })}
    </Mtds.Grid>
  )
}
